import { request } from '@umijs/max';
import type { Client, ClientListParams } from './clients';

/**
 * 过滤并序列化查询参数
 *
 * @param params - 原始查询对象
 */
const buildClientParams = (
  params?: ClientListParams,
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const result: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        result[key] = value.join(',');
      }
      return;
    }

    result[key] = value as string | number;
  });

  return Object.keys(result).length ? result : undefined;
};

/**
 * 客户收藏夹
 */
export interface ClientCollection {
  /** 收藏夹 UUID */
  uuid: string;
  /** 收藏夹名称 */
  name: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；缺失或为空表示根收藏夹 */
  parentUuid?: string | null;
  /** 子收藏夹（前端组装树时使用） */
  children?: ClientCollection[];
  /** 创建者 UUID */
  createdByUuid: string;
  /** 客户数量 */
  clientCount?: number;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
}

/**
 * 创建收藏夹参数
 */
export interface CreateClientCollectionDto {
  /** 收藏夹名称 */
  name: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；缺失或为空表示根收藏夹 */
  parentUuid?: string | null;
}

/**
 * 更新收藏夹参数
 */
export interface UpdateClientCollectionDto {
  /** 收藏夹名称 */
  name?: string;
  /** 收藏夹描述 */
  description?: string;
  /** 不传时保留原上级；传 null 表示移动到根收藏夹 */
  parentUuid?: string | null;
}

/**
 * 收藏夹客户列表响应
 */
export interface CollectionClientsResponse {
  /** 数据列表 */
  data: Client[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 单页数量 */
  limit: number;
}

/**
 * 批量添加客户到收藏夹响应
 */
export interface BatchAddClientsResponse {
  /** 是否全部成功 */
  success: boolean;
  /** 提示信息 */
  message: string;
  /** 成功数量 */
  successCount: number;
  /** 失败数量 */
  failCount: number;
  /** 失败列表 */
  failList?: Array<{ clientUuid: string; reason: string }>;
}

/**
 * 客户收藏夹 API
 */
export const clientCollectionApi = {
  /**
   * 获取所有收藏夹列表
   */
  getCollections: () => {
    return request<ClientCollection[]>('/bff/luole-hunter/client-collections');
  },

  /**
   * 获取收藏夹详情
   *
   * @param uuid - 收藏夹 UUID
   */
  getCollection: (uuid: string) => {
    return request<ClientCollection>(
      `/bff/luole-hunter/client-collections/${uuid}`,
    );
  },

  /**
   * 创建收藏夹
   *
   * @param data - 创建收藏夹数据
   */
  createCollection: (data: CreateClientCollectionDto) => {
    return request<ClientCollection>('/bff/luole-hunter/client-collections', {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新收藏夹
   *
   * @param uuid - 收藏夹 UUID
   * @param data - 更新收藏夹数据
   */
  updateCollection: (uuid: string, data: UpdateClientCollectionDto) => {
    return request<ClientCollection>(
      `/bff/luole-hunter/client-collections/${uuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除收藏夹
   *
   * @param uuid - 收藏夹 UUID
   */
  deleteCollection: (uuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/client-collections/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 获取收藏夹中的客户列表
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param params - 查询参数
   */
  getCollectionClients: (collectionUuid: string, params?: ClientListParams) => {
    const queryParams = buildClientParams(params);
    const options = queryParams ? { params: queryParams } : {};
    return request<CollectionClientsResponse>(
      `/bff/luole-hunter/client-collections/${collectionUuid}/clients`,
      options,
    );
  },

  /**
   * 添加客户到收藏夹
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param clientUuid - 客户 UUID
   */
  addClientToCollection: (collectionUuid: string, clientUuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/client-collections/${collectionUuid}/clients`,
      {
        method: 'POST',
        data: { clientUuid },
      },
    );
  },

  /**
   * 从收藏夹移除客户
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param clientUuid - 客户 UUID
   */
  removeClientFromCollection: (collectionUuid: string, clientUuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/client-collections/${collectionUuid}/clients/${clientUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 批量添加客户到收藏夹
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param clientUuids - 客户 UUID 列表
   */
  batchAddClientsToCollection: (
    collectionUuid: string,
    clientUuids: string[],
  ) => {
    return request<BatchAddClientsResponse>(
      `/bff/luole-hunter/client-collections/${collectionUuid}/clients/batch`,
      {
        method: 'POST',
        data: { clientUuids },
      },
    );
  },
};
